import { Injectable, Inject } from '@angular/core';
import { StorageService } from '../storage/storage.service';
import { DatabaseService } from '../storage/database.service';
import { WebSocketsService } from '../websockets/websockets.service';
import { ISmartenitConfig } from '../smartenit-config.interface';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { AppConfigurationService } from '../common/app-configuration.service';
import { EventsManagerService } from '../common/events-manager.service';
import { DeviceDetectorService } from 'ngx-device-detector';

const TOKEN_AHEAD_EXPIRATION = 60; // seconds

@Injectable()
export class AuthService {
    private excludedInstances = ['i18n_data'];

    private clientSecret: string;
    private clientId: string;
    private webSocketsService: WebSocketsService;

    private refreshTokenTimer: any = -1;
    private _tokenWillExpire: Subject<any>;

    private tokenManagement: boolean = false;
    private _authenticated: boolean = false;
    private _platform: string = '';
    public static APP_PLATFORM = 'app';
    public static BACKEND_PLATFORM = 'backend';
    public static WEBAPP_PLATFORM = 'webapp';

    public getPlatform(): string {
        return this._platform;
    }

    public setPlatform(plat: string) {
        this._platform = plat;
        this.initTokenManagementMode();
    }

    get tokenWillExpire(): Observable<any> {
        return this._tokenWillExpire.asObservable();
    }

    get authenticated(): boolean {
        return this._authenticated;
    }

    set authenticated(value: boolean) {
        this._authenticated = value;
        this.storage.set({
            key: 'authenticated',
            value,
        });
    }

    constructor(
        private storage: StorageService,
        private database: DatabaseService,
        private eventsManagerService: EventsManagerService,
        private AppConfiguration: AppConfigurationService,
        private deviceService: DeviceDetectorService
    ) {
        this._tokenWillExpire = new Subject<any>();

        if (AppConfiguration.currentConfig.clientId) {
            this.setClientId(AppConfiguration.currentConfig.clientId);
        }

        if (AppConfiguration.currentConfig.clientSecret) {
            this.setClientSecret(AppConfiguration.currentConfig.clientSecret);
        }

        const authenticated = this.storage.get('authenticated');
        this.authenticated = !!authenticated;
        this.initTokenManagementMode();
    }

    public setWebSocketsService(webSocketsService: WebSocketsService) {
        this.webSocketsService = webSocketsService;
    }

    public clearDB(excludedInstances?: string[]) {
        let excluded = [...this.excludedInstances];
        if (excludedInstances) {
            excluded = excluded.concat(...excludedInstances);
        }
        this.database.clear(excluded);
    }

    public logout(resource?: string) {
        if (this.webSocketsService && this.webSocketsService.isConnected) {
            this.webSocketsService.disconnect();
        }

        if (this.refreshTokenTimer) {
            clearTimeout(this.refreshTokenTimer);
        }

        if (resource !== AuthService.WEBAPP_PLATFORM.toUpperCase()) {
            this.storage.clear();
        }

        this.clearDB();
        this.AppConfiguration.restoreInitialConfiguration();

        this.eventsManagerService.trigger(EventsManagerService.ON_USER_SIGN_OUT);
    }

    public isLoggedIn() {
        if (!this.tokenManagement) {
            return this.authenticated;
        }
        const loggedIn = this.getAccessToken() !== null && this.getUser() !== null;

        if (loggedIn && this.refreshTokenTimer == -1) {
            const accessTokenRegistration = this.storage.get('ATR');
            const storedTokenLifetime = this.storage.get('ATL');
            if (accessTokenRegistration && storedTokenLifetime) {
                const tokenTime = this.retrieveAccessTokenTime(accessTokenRegistration, storedTokenLifetime);
                if (tokenTime.tokenCreation > tokenTime.tokenLifetime) {
                    this._tokenWillExpire.next(true);
                } else {
                    tokenTime.tokenLifetime = tokenTime.tokenLifetime - tokenTime.tokenCreation;

                    this.startRefreshTokenTimeout(tokenTime.tokenLifetime);
                }
            }
        }

        return loggedIn;
    }

    private retrieveAccessTokenTime(accessTokenRegistration: any, storedTokenLifetime: any) {
        let tokenCreation = parseInt(accessTokenRegistration.toString());
        tokenCreation = Math.floor((Date.now() - tokenCreation) / 1000);
        const tokenLifetime: number = parseInt(storedTokenLifetime.toString());

        return { tokenCreation, tokenLifetime };
    }

    public isAccessTokenExpired(accessTokenRegistration?: any, storedTokenLifetime?: any) {
        if (!accessTokenRegistration || !this.startRefreshTokenTimeout) {
            accessTokenRegistration = this.storage.get('ATR');
            storedTokenLifetime = this.storage.get('ATL');
            if (accessTokenRegistration && storedTokenLifetime) {
                const {
                    tokenCreation,
                    tokenLifetime
                } = this.retrieveAccessTokenTime(accessTokenRegistration, storedTokenLifetime);
                return tokenCreation > tokenLifetime;
            }
            return true;
        } else {
            const {
                tokenCreation,
                tokenLifetime
            } = this.retrieveAccessTokenTime(accessTokenRegistration, storedTokenLifetime);
            return tokenCreation > tokenLifetime;
        }
    }

    public getAccessToken() {
        return this.storage.get('AT');
    }

    public getRefreshToken() {
        return this.storage.get('RT');
    }

    public setClientId(clientId: string) {
        this.clientId = clientId;
    }

    public setClientSecret(clientSecret: string) {
        this.clientSecret = clientSecret;
    }

    public getClientId() {
        return this.clientId;
    }

    public getClientSecret() {
        return this.clientSecret;
    }

    public startRefreshTokenTimeout(ttl: number) {
        const checkExpiration = ttl < TOKEN_AHEAD_EXPIRATION ? ttl : ttl - TOKEN_AHEAD_EXPIRATION;

        if (this.refreshTokenTimer) {
            clearTimeout(this.refreshTokenTimer);
        }

        this.refreshTokenTimer = setTimeout(() => {
            this._tokenWillExpire.next(true);
        }, checkExpiration * 1000);
    }

    public getNewAccessTokenFromRefreshToken(skipIfIsStillValid = false) {
        if (!this.tokenManagement) {
            return;
        }

        if (skipIfIsStillValid) {
            const accessTokenRegistration = this.storage.get('ATR');
            const storedTokenLifetime = this.storage.get('ATL');
            if (accessTokenRegistration && storedTokenLifetime &&
                !this.isAccessTokenExpired(accessTokenRegistration, storedTokenLifetime)) {
                console.log('Skipping token refresh...');
                this.eventsManagerService.trigger(EventsManagerService.ON_RESTORE_NETWORK_STATUS);
                return;
            }
        }

        this._tokenWillExpire.next(true);
    }

    public initTokenManagementMode() {
        const deviceInfo = this.deviceService.getDeviceInfo();
        if (
            deviceInfo.device === 'android' ||
            deviceInfo.device === 'ios' ||
            deviceInfo.device === 'iphone'
            || this.getPlatform() === 'app'
        ) {
            this.tokenManagement = true;
        } else {
            this.tokenManagement = false;
        }
    }

    public setAccessToken(accessToken: string, ttl: number) {
        if (!this.tokenManagement) {
            return;
        }
        this.startRefreshTokenTimeout(ttl);

        this.storage.set({ key: 'ATR', value: Date.now() });
        this.storage.set({ key: 'ATL', value: ttl });

        return this.storage.set({ key: 'AT', value: accessToken });
    }

    public setRefreshToken(refreshToken: string) {
        if (!this.tokenManagement) {
            return;
        }
        if (refreshToken) {
            return this.storage.set({ key: 'RT', value: refreshToken });
        }
    }

    public getUser() {
        return this.storage.get('US');
    }

    public setUser(user: any) {
        return this.storage.set({ key: 'US', value: user });
    }

    public setAccount(account: any) {
        return this.storage.set({ key: 'AC', value: account });
    }

    public getAccount() {
        return this.storage.get('AC');
    }

    public getTokenMangement() {
        return this.tokenManagement;
    }

    public setTokenManagement(tokenManagement: boolean) {
        this.tokenManagement = tokenManagement;
    }
}
